/**
 * Hero backdrop: the always-on static aurora (the page's original look, and the
 * reduced-motion / mobile / no-WebGL fallback) plus, when capable, the lazy
 * WebGL particle scene layered on top. `three` is a separate chunk fetched on
 * idle after first paint, so it never blocks load or ships to phones.
 */

import { lazy, Suspense, useEffect, useState } from "react";

import { shouldRender3D } from "../lib/motionEnv";

const HeroCanvas = lazy(() => import("./HeroCanvas"));

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

export function HeroBackground() {
  const [enable3D, setEnable3D] = useState(false);

  useEffect(() => {
    if (!shouldRender3D()) return;
    const w = window as IdleWindow;
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    if (w.requestIdleCallback) {
      idleId = w.requestIdleCallback(() => setEnable3D(true), { timeout: 1500 });
    } else {
      timeoutId = window.setTimeout(() => setEnable3D(true), 400);
    }
    return () => {
      if (idleId !== undefined && w.cancelIdleCallback) w.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      {/* Static aurora — always rendered; the graceful fallback. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(900px 540px at 90% 0%, rgba(15,118,110,0.10), transparent 60%), radial-gradient(720px 420px at 10% 90%, rgba(122,61,255,0.08), transparent 60%)",
          animation: "m2s-aurora 18s ease-in-out infinite",
        }}
      />
      {enable3D && (
        <Suspense fallback={null}>
          <HeroCanvas />
        </Suspense>
      )}
    </div>
  );
}
